import {
  LEGACY_KEY_TO_RESOURCE,
  LEGACY_MANAGED_KEYS,
  emptyResource,
  type JsonValue,
  type ResourceDocument,
  type ResourceType,
  type SyncOperation
} from '../domain/resources';
import type { LocalDatabase } from './local-database';

const CLIENT_SCHEMA_VERSION_KEY = 'client-schema-version';
const LEGACY_STORAGE_MIGRATION_KEY = 'migration:legacy-storage-v1';

export interface LegacyImport {
  resources: ResourceDocument[];
  operations: SyncOperation[];
}

function operationId(): string {
  return globalThis.crypto?.randomUUID?.()
    || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

let migrationOperationOrder = 0;

export function parseLegacyValue(raw: string): JsonValue {
  try {
    return JSON.parse(raw) as JsonValue;
  } catch {
    return raw;
  }
}

export function createLegacyImport(
  storage: Record<string, string>,
  existingResources: ResourceDocument[] = []
): LegacyImport {
  const resources = new Map<ResourceType, ResourceDocument>(
    existingResources.map(resource => [resource.type, structuredClone(resource)])
  );
  const operations: SyncOperation[] = [];
  const now = new Date().toISOString();

  for (const [key, raw] of Object.entries(storage)) {
    const type = LEGACY_KEY_TO_RESOURCE[key];
    if (!type) continue;
    const resource = resources.get(type) || emptyResource(type);
    if (Object.hasOwn(resource.payload, key)) continue;
    const value = parseLegacyValue(raw);
    resource.payload[key] = value;
    resource.localRevision += 1;
    resource.updatedAt = now;
    resources.set(type, resource);
    if (type !== 'cache') {
      operations.push({
        id: operationId(),
        resourceType: type,
        documentKey: key,
        path: [],
        action: 'set',
        value,
        createdAt: now,
        localOrder: migrationOperationOrder += 1
      });
    }
  }

  return { resources: [...resources.values()], operations };
}

export function readLegacyBrowserStorage(storage: Storage | null): Record<string, string> {
  if (!storage) return {};
  const snapshot: Record<string, string> = {};
  for (const key of LEGACY_MANAGED_KEYS) {
    try {
      const value = storage.getItem(key);
      if (value !== null) snapshot[key] = value;
    } catch {
      // Storage can be disabled by browser privacy settings.
    }
  }
  return snapshot;
}

export async function runClientMigrations(
  database: LocalDatabase,
  legacyStorage: Record<string, string>
): Promise<ResourceDocument[]> {
  let resources = await database.listResources();
  const legacyDone = await database.getMetadata(LEGACY_STORAGE_MIGRATION_KEY);

  if (!legacyDone) {
    const imported = createLegacyImport(legacyStorage, resources);
    for (const resource of imported.resources) await database.putResource(resource);
    await database.putOperations(imported.operations);
    await database.setMetadata(LEGACY_STORAGE_MIGRATION_KEY, new Date().toISOString());
    resources = imported.resources;
  }

  // Sequential, idempotent client migrations belong here as the schema evolves.
  for (const resource of resources) {
    if (!resource.schemaVersion) {
      resource.schemaVersion = 1;
      await database.putResource(resource);
    }
  }
  await database.setMetadata(CLIENT_SCHEMA_VERSION_KEY, '1');
  return resources;
}

export async function importLegacySnapshot(
  database: LocalDatabase,
  snapshot: Record<string, string>,
  migrationKey: string
): Promise<ResourceDocument[]> {
  if (await database.getMetadata(migrationKey)) return database.listResources();
  const imported = createLegacyImport(snapshot, await database.listResources());
  for (const resource of imported.resources) await database.putResource(resource);
  await database.putOperations(imported.operations);
  await database.setMetadata(migrationKey, new Date().toISOString());
  return imported.resources;
}
